import axios from 'axios';
import LogRocket from 'logrocket';
import TagManager from 'react-gtm-module';
import { push } from 'connected-react-router';
import { startSubmit, stopSubmit } from 'redux-form';
import { takeLatest, takeEvery, put, select, call } from 'redux-saga/effects';
import _ from 'lodash';
import { isStatusOk, redirectToUrl, constructGTM } from '../utilities/helpers';
import {
  LOGIN,
  LOGOUT,
  CHECK_AUTHENTICATED,
  FORGOT_PW,
  NEW_PW_ENTERED,
  SIGN_UP,
  GET_USER_PROFILE,
  UPDATE_USER_PROFILE,
  GET_SKILLSET_OPTIONS_OF_TYPE,
  GET_JOBS,
  CREATE_JOB,
  UPDATE_JOB,
  RESEND_VERIFICATION_EMAIL,
  BUILD_TOOLS,
  UPDATE_NOTIFICATION_SETTINGS,
  GET_BUSINESS_ATTRIBUTES,
  UPDATE_BUSINESS_ATTRIBUTES,
  CONFIRM_APPROVE_BUILDER,
} from './actions/actionTypes';
import { addNotification } from './actions/notification';
import {
  loginSuccess,
  loginFailure,
  logoutSuccessAction,
  logoutForceAction,
  forgotPwSuccess,
  forgotPwFailure,
  changeLoginScreenAction,
  newPwSuccess,
  newPwFailure,
  resendVerificatonEmailFailure,
  resendVerificatonEmailSuccess,
} from './actions/login';
import {
  signUpSuccess,
  signUpFailure,
  resetRegistrationState,
} from './actions/registration';
import {
  getUserProfileSuccess,
  getUserProfileFailure,
  updateUserProfileSuccess,
  updateUserProfileFailure,
  resetUserProfileState,
  setUserProfileCustomAttribute,
  getBusinessAttributesSuccess,
  getBusinessAttributesFailure,
  updateBusinessAttributesSuccess,
  updateBusinessAttributesFailure,
} from './actions/userProfile';
import {
  getSkillsetOfTypeSuccess,
  resetSkillsetState,
} from './actions/skillset';
import {
  getJobsSuccess,
  getJobsFailure,
  createJobFailure,
  createJobSuccess,
  updateJobSuccess,
  updateJobFailure,
  resetJobsState,
} from './actions/jobs';
import {
  updateNotificationSettingsSuccess,
  updateNotificationSettingsFailure,
} from './actions/settings';
import { closeModal, resetModalState } from './actions/modal';
import {
  confirmApproveBuilderSuccess,
  confirmApproveBuilderFailure,
} from './actions/builderApproval';
import { selectVerificationCode, selectVerificationEmail } from './selectors';
import {
  LOGOUT_API,
  FORGOT_PASSWORD_API,
  RESET_PASSWORD_API,
  SIGN_UP_API,
  NOTIFICATON_LEVELS,
  GET_SKILLSET_OPTIONS_API,
  USER_JOBS_API,
  API_KEY_ACCOUNTS,
  API_KEY_BUILDERS,
  RESEND_VERIFICATION_EMAIL_API,
  LOGIN_SCREENS,
  ACCEL_ROLE_TYPES,
  PARDOT_ROLE_TYPE_MAP,
  PARDOT_SIGNUP_REQUEST,
  UPLOAD_API,
  getAccelUrl,
} from '../constants';
import {
  loginApi,
  getUser,
  createActivity,
  createAccelSession,
  updateUserProfile,
  getUserBusinesses,
  updateUserBusiness,
  confirmApproveBuilder,
} from './apis';
import {
  redirectBuildToolsFailure,
  redirectBuildToolsSuccess,
  resetNavigationState,
} from './actions/navigation';

import { getNextNofificationStatus } from '../components/NotificationSettingsContainer';
import { resetBuilderProjectGalleryImages } from './actions/builderPics';

export function* loginSaga(action) {
  try {
    yield put(startSubmit('LoginForm'));
    const response = yield call(
      loginApi,
      action.credentials.email,
      action.credentials.password,
    );

    if (isStatusOk(response)) {
      // get the user profile
      const getUserResponse = yield call(getUser, response.data.accessToken);
      if (isStatusOk(getUserResponse)) {
        yield call(createActivity, response.data.accessToken, 'Logged In');

        // return;
        // identify user to log rocket
        const userAttributes = {};
        getUserResponse.data.UserAttributes.forEach(attribute => {
          userAttributes[attribute.Name] = attribute.Value;
        });
        LogRocket.identify(getUserResponse.data.Username, userAttributes);

        // Log login event to GTM
        TagManager.initialize(
          constructGTM({
            dataLayer: {
              dlv_userId: getUserResponse.data.Username,
              event: 'User Logged In',
            },
          }),
        );

        // check the role if architect
        const roleTypeAttribute = getUserResponse.data.UserAttributes.find(
          attribute => attribute.Name === 'custom:roleType',
        );
        const roleType =
          roleTypeAttribute != null ? roleTypeAttribute.Value : null;
        if (roleType != null && ACCEL_ROLE_TYPES.includes(roleType)) {
          // create an accel session
          const createAccelSessionsResponse = yield call(
            createAccelSession,
            response.data.accessToken,
          );
          if (isStatusOk(createAccelSessionsResponse)) {
            // Log non-builders login event to GTM
            TagManager.initialize(
              constructGTM({
                dataLayer: {
                  event: 'Non-Builder logged in & redirected to Accel',
                },
              }),
            );
            // redirect to Accel
            yield put(stopSubmit('LoginForm'));
            yield call(
              redirectToUrl,
              createAccelSessionsResponse.data.accelUrl,
            );
          } else {
            LogRocket.captureMessage('createAccelSessionsResponse Failed', {
              source: 'loginSaga',
              message:
                response.data.message || 'createAccelSessionsResponse Failed',
            });
            yield put(loginFailure(response.data.message));
            yield put(
              stopSubmit('LoginForm', { _error: response.data.message }),
            );
          }
        } else {
          // role is not an accel supported role, log him in to myBuilder
          const userInfo = response.data;
          localStorage.setItem('accessToken', userInfo.accessToken);
          localStorage.setItem('idToken', userInfo.idToken);
          yield put(loginSuccess('success'));
          // save builder profile in redux
          yield put(
            getUserProfileSuccess(
              getUserResponse.data.Username,
              getUserResponse.data.UserAttributes,
            ),
          );
          yield put(stopSubmit('LoginForm'));
          yield put(push('/my-jobs'));

          yield put(
            addNotification(
              'Successfully logged in.',
              NOTIFICATON_LEVELS.SUCCESS,
            ),
          );
        }
      } else {
        LogRocket.captureMessage("Couldn't get the user's profile", {
          source: 'loginSaga',
          message: response.data.message || "Couldn't get the user's profile",
        });
        yield put(loginFailure(response.data.message));
        yield put(stopSubmit('LoginForm', { _error: response.data.message }));
      }
    } else {
      LogRocket.captureMessage('Login Failed', {
        source: 'loginSaga',
        message: response.data.message || 'Failed to login the user.',
      });
      yield put(loginFailure(response.data.message));
      yield put(stopSubmit('LoginForm', { _error: response.data.message }));

      if (response.data.code === 'UserNotConfirmedException') {
        yield put(
          changeLoginScreenAction(LOGIN_SCREENS.RESEND_VERIFICATION_EMAIL),
        );
        yield put(
          addNotification(
            'Your email has not been verified. Please check the verification email or request a new one.',
            NOTIFICATON_LEVELS.ERROR,
            true,
          ),
        );
      }
    }
  } catch (err) {
    LogRocket.captureException(err, {
      source: 'loginSaga',
      message: 'The login error message Connection error',
    });
    yield put(loginFailure('The login error message Connection error'));
    yield put(stopSubmit('LoginForm', { _error: 'Connection error' }));
  }
}

export function* buildToolsSaga(action) {
  try {
    // create an accel session
    const createAccelSessionsResponse = yield call(
      createAccelSession,
      action.accessToken,
    );
    if (isStatusOk(createAccelSessionsResponse)) {
      // redirect to Accel
      yield put(redirectBuildToolsSuccess());
      yield call(
        redirectToUrl,
        `${createAccelSessionsResponse.data.accelUrl}&url=${getAccelUrl(
          action.accelType,
        )}`,
        true,
      );
      // yield call(redirectToUrl, getAccelUrl(action.accelType), true);
    } else {
      LogRocket.captureMessage('redirectBuildToolsFailure', {
        source: 'buildToolsSaga',
        message: 'Could not create a session',
      });
      yield put(redirectBuildToolsFailure('Could not create a session'));
      yield put(
        addNotification(
          'We could not process your request at the moment. Please try again later.',
          NOTIFICATON_LEVELS.ERROR,
        ),
      );
    }
  } catch (err) {
    LogRocket.captureException(err, {
      source: 'buildToolsSaga',
      message: 'Exception encountered. Could not create a session',
    });
    yield put(
      redirectBuildToolsFailure(
        'Exception encountered. Could not create a session',
      ),
    );
    yield put(
      addNotification(
        'We could not process your request at the moment. Please try again later.',
        NOTIFICATON_LEVELS.ERROR,
      ),
    );
  }
}

export function* logoutSaga(action) {
  try {
    yield call(createActivity, action.accessToken, 'Logged Out');

    const response = yield axios({
      method: 'post',
      url: LOGOUT_API,
      headers: {
        Authorization: `Bearer ${action.accessToken}`,
        'x-api-key': API_KEY_ACCOUNTS,
      },
    });

    // clear redux state
    yield put(resetJobsState());
    yield put(resetModalState());
    yield put(resetNavigationState());
    yield put(resetRegistrationState());
    yield put(resetSkillsetState());
    yield put(resetUserProfileState());
    if (isStatusOk(response)) {
      yield localStorage.clear();
      yield put(logoutSuccessAction('success logout from cognito'));
      yield put(push('/'));
      yield put(
        addNotification('Successfully logged out', NOTIFICATON_LEVELS.SUCCESS),
      );
    } else {
      LogRocket.captureMessage('Failed to logout cognito. Forced logout', {
        source: 'logoutSaga',
        message: 'Failed to logout cognito. Forced logout',
      });
      // Force logout
      yield localStorage.clear();
      yield put(logoutForceAction('Failed to logout cognito. Forced logout'));
      yield put(push('/'));
      yield put(
        addNotification('Successfully logged out', NOTIFICATON_LEVELS.SUCCESS),
      );
    }
  } catch (err) {
    LogRocket.captureException(err, {
      source: 'logoutSaga',
      message: 'Failed to logout cognito. Forced logout',
    });
    // Force logout
    yield localStorage.clear();
    yield put(logoutForceAction('Failed to logout cognito. Forced logout'));
    yield put(push('/'));
    yield put(
      addNotification('Successfully logged out', NOTIFICATON_LEVELS.SUCCESS),
    );
  }
}

export function* checkAuthSaga(action) {
  try {
    if (action.accessToken) {
      const response = yield call(getUser, action.accessToken);
      if (isStatusOk(response)) {
        yield put(loginSuccess('AUTHENTICATED success'));
        // save builder profile in redux
        yield put(
          getUserProfileSuccess(
            response.data.Username,
            response.data.UserAttributes,
          ),
        );
      } else if (response.data.code === 'NotAuthorizedException') {
        LogRocket.captureMessage('NotAuthorizedException', {
          source: 'checkAuthSaga',
          message: 'Failed authentication. Forced logout',
        });
        // Force logout
        yield localStorage.clear();
        yield put(logoutForceAction('Failed authentication. Forced logout'));
        if (!action.noNotification) {
          yield put(
            addNotification(
              'Session expired. Please login again.',
              NOTIFICATON_LEVELS.WARNING,
            ),
          );
        }
      }
    } else {
      LogRocket.captureMessage('bad accessToken', {
        source: 'checkAuthSaga',
        message: 'Failed authentication. Forced logout',
      });
      yield localStorage.clear();
      yield put(logoutForceAction('Failed authentication. Forced logout'));
    }
  } catch (err) {
    LogRocket.captureException(err, {
      source: 'checkAuthSaga',
      message: 'Failed authentication. Forced logout catch error',
    });
    // Force logout
    yield localStorage.clear();
    yield put(
      logoutForceAction('Failed authentication. Forced logout catch error'),
    );
    if (!action.noNotification) {
      yield put(addNotification('Logged Out.', NOTIFICATON_LEVELS.WARNING));
    }
  }
}

export function* forgotPwSaga(action) {
  try {
    yield put(startSubmit('ForgotPwForm'));
    const response = yield axios({
      method: 'post',
      data: {
        username: action.credentials.email,
      },
      url: `${FORGOT_PASSWORD_API}`,
      headers: {
        'Content-Type': 'application/json',
        'x-api-key': API_KEY_ACCOUNTS,
      },
      crossDomain: true,
      validateStatus(status) {
        return status >= 200 && status <= 403; // default
      },
    });

    if (isStatusOk(response)) {
      yield put(forgotPwSuccess(response.data.CodeDeliveryDetails));
      yield put(stopSubmit('ForgotPwForm'));
      yield put(changeLoginScreenAction('verification'));
    } else {
      if (
        response.data.message ===
        'Cannot reset password for the user as there is no registered/verified email or phone_number'
      ) {
        response.data.message =
          'Cannot reset password. Email address is not verified.';
      }
      LogRocket.captureMessage('Unverified Email', {
        source: 'forgotPwSaga',
        message: 'Cannot reset password. Email address is not verified.',
      });
      yield put(forgotPwFailure(response.data.message));
      yield put(stopSubmit('ForgotPwForm', { _error: response.data.message }));
    }
  } catch (err) {
    LogRocket.captureException(err, {
      source: 'forgotPwSaga',
      message: 'The login error message Connection error',
    });
    yield put(forgotPwFailure('The login error message Connection error'));
    yield put(stopSubmit('ForgotPwForm', { _error: 'Connection error' }));
  }
}

export function* newPwSaga(action) {
  try {
    yield put(startSubmit('NewPwForm'));
    const username = yield select(selectVerificationEmail());
    const passwordResetCode = yield select(selectVerificationCode());
    const response = yield axios({
      method: 'post',
      data: {
        username,
        newPassword: action.newPw.password,
        passwordResetCode: passwordResetCode.verificationCode,
      },
      headers: {
        'Content-Type': 'application/json',
        'x-api-key': API_KEY_ACCOUNTS,
      },
      url: `${RESET_PASSWORD_API}`,
      crossDomain: true,
      validateStatus(status) {
        return status >= 200 && status <= 403; // default
      },
    });

    if (isStatusOk(response)) {
      yield put(newPwSuccess(response.data));
      yield put(stopSubmit('NewPwForm'));
      yield put(changeLoginScreenAction('newPwSuccess'));
    } else {
      LogRocket.captureMessage('new password failure', {
        source: 'newPwSaga',
        message: response.data.message || 'new password failure',
      });
      yield put(newPwFailure(response.data.message));
      yield put(stopSubmit('NewPwForm', { _error: response.data.message }));
    }
  } catch (err) {
    LogRocket.captureException(err, {
      source: 'newPwSaga',
      message: 'The login error message Connection error',
    });
    yield put(forgotPwFailure('The login error message Connection error'));
    yield put(stopSubmit('ForgotPwForm', { _error: 'Connection error' }));
  }
}

const getUpdatedPardotFields = data => {
  const pardotData = Object.assign({}, data);
  pardotData.legal = 'Yes';
  delete pardotData.personal_collection;
  pardotData['custom:marketingOptIn'] =
    pardotData['custom:marketingOptIn'] === 'Y' ? 'Yes' : 'No';
  pardotData['custom:roleType'] =
    PARDOT_ROLE_TYPE_MAP[pardotData['custom:roleType']];
  delete pardotData['custom:buildingLicNo'];
  delete pardotData['custom:buildingLicExpMask'];
  delete pardotData['custom:profileHeadline'];
  delete pardotData['custom:skillsetBuild'];
  delete pardotData['custom:businessAddress'];
  delete pardotData['custom:skillsetLook'];
  delete pardotData['custom:buildType'];
  delete pardotData['custom:lookType'];
  delete pardotData['custom:location'];
  delete pardotData['custom:serviceAAdd'];
  return pardotData;
};

export function* signUpSaga(action) {
  // console.log('came here in signUpSaga');
  // console.log({ action });
  const data = Object.assign({}, action.data);
  data.attributes.name = `${data.attributes['custom:firstName']} ${
    data.attributes['custom:surname']
  }`;

  // deleting temporary fields

  const pardotData = getUpdatedPardotFields(action.data.attributes);
  delete data.attributes['custom:firstName'];
  delete data.attributes['custom:surname'];

  // console.log('data', data);

  // uploading images
  let file;
  let res;
  if (data.attributes.logo) {
    // eslint-disable-next-line prefer-destructuring
    file = data.attributes.logo[0];
    res = yield uploadImage(file, file.name, 'logo');
    data.attributes['custom:logo'] = `${res.fileId}`;
  }
  delete data.attributes.logo;

  if (data.attributes.coverPhoto) {
    // eslint-disable-next-line prefer-destructuring
    file = data.attributes.coverPhoto[0];
    res = yield uploadImage(file, file.name, 'cover');
    data.attributes['custom:coverPhoto'] = `${res.fileId}`;
  }
  delete data.attributes.coverPhoto;

  const gallery = [];
  if (data.attributes.projectGalleryImages)
    for (let i = 0; i < data.attributes.projectGalleryImages.length; i += 1) {
      // eslint-disable-next-line prefer-destructuring
      file = data.attributes.projectGalleryImages[i][0];
      res = yield uploadImage(file, file.name, 'gallery');
      if (res.fileId) gallery.push(res.fileId);
    }

  data.attributes['custom:gallery'] = gallery.join(',');
  delete data.attributes.projectGalleryImages;
  data.attributes['custom:showInSearch'] =
    data.attributes.showInSearch === true ? '1' : '0';
  delete data.attributes.showInSearch;
  // console.log('Newdata', data);
  // return;

  try {
    const response = yield axios({
      method: 'post',
      headers: {
        'Content-Type': 'application/json',
        'x-api-key': API_KEY_ACCOUNTS,
      },
      data,
      url: `${SIGN_UP_API}/my-build`,
    });

    if (isStatusOk(response)) {
      // Submitting to pardot, pardot will accept only x-www-form-urlencoded
      const formData = new URLSearchParams(pardotData);

      fetch(PARDOT_SIGNUP_REQUEST, {
        method: 'POST',
        body: formData.toString(),
        json: false,
        mode: 'no-cors',
        headers: {
          // 'Content-Type': 'application/x-www-form-urlencoded',
        },
      });

      yield put(signUpSuccess());
      yield put(resetBuilderProjectGalleryImages());
    } else {
      LogRocket.captureMessage('Signup failed.', {
        source: 'signUpSaga',
        message: response.data.message || 'Signup failed.',
      });
      yield put(signUpFailure());
      yield put(
        addNotification(
          'Something is wrong. Please try again later.',
          NOTIFICATON_LEVELS.ERROR,
        ),
      );
    }
  } catch (err) {
    LogRocket.captureException(err, {
      source: 'signUpSaga',
      message: 'Signup failed.',
    });
    yield put(signUpFailure());
    yield put(
      addNotification(
        'Something is wrong. Please try again later.',
        NOTIFICATON_LEVELS.ERROR,
      ),
    );
  }
}

export function* getUserProfileSaga(action) {
  try {
    const response = yield call(getUser, action.accessToken);

    if (isStatusOk(response)) {
      yield put(
        getUserProfileSuccess(
          response.data.Username,
          response.data.UserAttributes,
        ),
      );
    } else {
      LogRocket.captureMessage("Couldn't get user's profile", {
        source: 'getUserProfileSaga',
        message: response.data.message || "Couldn't get user's profile",
      });
      yield put(getUserProfileFailure());
      yield put(
        addNotification(
          'Something is wrong. Please try again later.',
          NOTIFICATON_LEVELS.ERROR,
        ),
      );
    }
  } catch (err) {
    LogRocket.captureException(err, {
      source: 'getUserProfileSaga',
      message: "Couldn't get user's profile.",
    });
    yield put(getUserProfileFailure());
    yield put(
      addNotification(
        'Something is wrong. Please try again later.',
        NOTIFICATON_LEVELS.ERROR,
      ),
    );
  }
}

export function* getBusinessAttributesSaga(action) {
  try {
    const response = yield call(getUserBusinesses, action.accessToken);
    if (isStatusOk(response)) {
      const { businesses } = response.data;
      if (businesses.length) {
        // eslint-disable-next-line prettier/prettier
        const businessAttributes = mapBusinessResponseToBusinessAttributes(
          businesses[0],
        );

        yield put(getBusinessAttributesSuccess(businessAttributes));
      } else {
        LogRocket.captureMessage('No associated business was found', {
          source: 'getBusinessAttributesSaga',
          message: response.data.message || 'No associated business was found',
        });
        // eslint-disable-next-line prettier/prettier
        yield put(getBusinessAttributesFailure());
        yield put(
          addNotification(
            'No associated business was found',
            NOTIFICATON_LEVELS.ERROR,
          ),
        );
      }
    } else {
      LogRocket.captureMessage("Couldn't get business attributes", {
        source: 'getBusinessAttributesSaga',
        message: response.data.message || "Couldn't get business attributes",
      });
      yield put(getBusinessAttributesFailure());
      yield put(
        addNotification(
          'Something is wrong. Please try again later.',
          NOTIFICATON_LEVELS.ERROR,
        ),
      );
    }
  } catch (err) {
    LogRocket.captureException(err, {
      source: 'getBusinessAttributesSaga',
      message: "Couldn't get business attributes.",
    });
    yield put(getBusinessAttributesFailure());
    yield put(
      addNotification(
        'Something is wrong. Please try again later.',
        NOTIFICATON_LEVELS.ERROR,
      ),
    );
  }
}

export function* updateUserProfileSaga(action) {
  // console.log({ action }, 'updateUserProfileSaga');
  const changedValues = Object.keys(action.userAttributes).filter(
    k => action.userAttributes[k] !== action.previousValues[k],
  );
  // console.log({ changedValues });
  // return;

  delete action.userAttributes.deactivate;
  delete action.userAttributes.priority;
  delete action.userAttributes.reason;
  delete action.userAttributes.other_reason;
  try {
    const response = yield call(
      updateUserProfile,
      action.accessToken,
      action.userAttributes,
    );

    if (isStatusOk(response)) {
      if (changedValues.length >= 1) {
        for (let i = 0; i < changedValues.length; i += 1) {
          if (changedValues[i] === 'custom:buildingLicExp') {
            yield call(
              createActivity,
              action.accessToken,
              'Licence Expiry Updated',
            );
          } else if (changedValues[i] === 'custom:mobileNumber') {
            yield call(
              createActivity,
              action.accessToken,
              'Mobile Number Updated',
            );
          } else if (changedValues[i] === 'custom:personalPhone') {
            yield call(
              createActivity,
              action.accessToken,
              'Primary Contact Number Updated',
            );
          } else if (changedValues[i] === 'custom:businessYears') {
            yield call(
              createActivity,
              action.accessToken,
              'Number Of Years Business Has Been Open Updated Updated',
            );
          } else if (changedValues[i] === 'custom:employeesCount') {
            yield call(
              createActivity,
              action.accessToken,
              'Number Of Permanent Employees Updated',
            );
          } else if (changedValues[i] === 'custom:newBuildsPerYear') {
            yield call(
              createActivity,
              action.accessToken,
              'New Builds Completed Over The Past Year Updated',
            );
          } else if (changedValues[i] === 'custom:renovationsPerYear') {
            yield call(
              createActivity,
              action.accessToken,
              'Renovations Completed Over The Past Year Updated',
            );
          }
        }
      }
      // console.log('Status OK');
      if (action.businessAttributes) {
        if (
          Object.prototype.hasOwnProperty.call(
            action.businessAttributes,
            'showInSearch',
          )
        ) {
          yield call(
            createActivity,
            action.accessToken,
            `Show In Search ${
              action.businessAttributes.showInSearch === true
                ? 'Enabled'
                : 'Disabled'
            }`,
          );
        }
        yield call(
          updateUserBusiness,
          action.accessToken,
          action.businessAttributes.businessNumber,
          action.businessAttributes,
        );
        yield getBusinessAttributesSaga({ accessToken: action.accessToken });
      }

      yield put(updateUserProfileSuccess(action.profileType));
      yield put(
        addNotification(
          'User profile has been successfully updated.',
          NOTIFICATON_LEVELS.SUCCESS,
        ),
      );
    } else {
      LogRocket.captureMessage('Failed to update user profile.', {
        source: 'updateUserProfileSaga',
        message: response.data.message || 'Failed to update user profile.',
      });
      yield put(
        addNotification(
          'Something is wrong. Please try again later.',
          NOTIFICATON_LEVELS.ERROR,
        ),
      );
    }
  } catch (err) {
    LogRocket.captureException(err, {
      source: 'updateUserProfileSaga',
      message: 'Failed to update user profile.',
    });
    yield put(updateUserProfileFailure(action.profileType));
    yield put(
      addNotification(
        'Something is wrong. Please try again later.',
        NOTIFICATON_LEVELS.ERROR,
      ),
    );
  }
}

export function* updateBusinessAttributesSaga(action) {
  try {
    // map to builder detail request
    // console.log('came here ', { action });
    let skillset = [];
    if (action.businessAttributes.businessSkillsetBuild) {
      const skillsetBuildList = action.businessAttributes.businessSkillsetBuild
        .split(' & ')
        .map(skillsetBuildItem => ({
          type: 'build',
          name: skillsetBuildItem,
        }));
      skillset = skillset.concat(skillsetBuildList);
    }
    if (action.businessAttributes.businessSkillsetLook) {
      const skillsetLookList = action.businessAttributes.businessSkillsetLook
        .split(' & ')
        .map(skillsetLookItem => ({
          type: 'look',
          name: skillsetLookItem,
        }));
      skillset = skillset.concat(skillsetLookList);
    }

    let mailingAddress = null;
    if (action.businessAttributes.businessMailingAddress) {
      mailingAddress = {
        fullAddress: action.businessAttributes.businessMailingAddress,
      };
    }

    let serviceArea = null;
    if (action.businessAttributes.businessServiceAreaType) {
      const [latitudeStr, longitudeStr] = action.businessAttributes
        .businessServiceAreaCoordinates
        ? action.businessAttributes.businessServiceAreaCoordinates.split(',')
        : [];
      const latitude = latitudeStr ? parseFloat(latitudeStr) : null;
      const longitude = longitudeStr ? parseFloat(longitudeStr) : null;
      serviceArea = {
        type: action.businessAttributes.businessServiceAreaType,
        fullAddress: action.businessAttributes.businessServiceAreaAddress,
        latitude,
        longitude,
        state: action.businessAttributes.businessServiceAreaState,
      };
    }

    // uploading images
    let logo = _.get(action.businessAttributes, 'logoImage.id');
    if (_.get(action, 'businessAttributes.logo') === 'removed') {
      logo = null;
    } else if (
      action.businessAttributes.logo &&
      action.businessAttributes.logo.length
    ) {
      // checking if new image is set
      const file = action.businessAttributes.logo[0];
      const res = yield uploadImage(file, file.name, 'logo');
      logo = `${res.fileId}`;
    }

    let coverPhoto = _.get(action.businessAttributes, 'coverPhotoImage.id');
    // console.log('coverPhoto', coverPhoto);
    // console.log('coverPhoto - logo', logo);
    if (_.get(action, 'businessAttributes.coverPhoto') === 'removed') {
      coverPhoto = null;
    } else if (
      action.businessAttributes.coverPhoto &&
      action.businessAttributes.coverPhoto.length
    ) {
      // checking if new image is set
      const file = action.businessAttributes.coverPhoto[0];
      const res = yield uploadImage(file, file.name, 'cover');
      coverPhoto = `${res.fileId}`;
    }
    // // eslint-disable-next-line prefer-destructuring
    // file = data.attributes.coverPhoto[0];
    // res = yield uploadImage(file, file.name, 'cover');
    // data.attributes['custom:coverPhoto'] = `${res.fileId}`;
    // delete data.attributes.coverPhoto;

    const galleryIds = [];
    const galleryImages = _.get(action.businessAttributes, 'galleryImages', []);
    for (let i = 0; i < galleryImages.length; i += 1) {
      const image = galleryImages[i];
      if (image.file) {
        const res = yield uploadImage(
          image.file[0],
          image.file[0].name,
          'gallery',
        );
        if (res.fileId) galleryIds.push(res.fileId);
      } else {
        galleryIds.push(image.id);
      }
    }

    const gallery = galleryIds.join(',');
    // console.log('updated', gallery);
    const businessDetails = {
      businessNumber: action.businessAttributes.businessNumber,
      name: action.businessAttributes.businessName,
      description: action.businessAttributes.businessDescription,
      position: action.businessAttributes.businessPosition,
      materialLocation: action.businessAttributes.materialLocation,
      mailingAddress,
      serviceArea,
      skillset,
      logo,
      coverPhoto,
      gallery,
      googlePlaceField: action.businessAttributes.googlePlaceField,
      googlePlaceId: action.businessAttributes.googlePlaceId,
    };

    // previous builders
    let prevMailingAddress = null;
    let PrevServiceArea = null;
    let prevSkillset = [];
    let prevLogo = _.get(action.previousBuisnessAttributes, 'logoImage.id');
    let prevCoverPhoto = _.get(
      action.previousBuisnessAttributes,
      'coverPhotoImage.id',
    );

    let previousBusinessDetails = null;

    let testBusinessDetails = null;
    let changedValues = null;

    if (action.previousBuisnessAttributes) {
      if (action.previousBuisnessAttributes.businessMailingAddress) {
        prevMailingAddress = {
          fullAddress: action.previousBuisnessAttributes.businessMailingAddress,
        };
      }

      if (action.previousBuisnessAttributes.businessServiceAreaType) {
        const [latitudeStr, longitudeStr] = action.previousBuisnessAttributes
          .businessServiceAreaCoordinates
          ? action.previousBuisnessAttributes.businessServiceAreaCoordinates.split(
              ',',
            )
          : [];
        const latitude = latitudeStr ? parseFloat(latitudeStr) : null;
        const longitude = longitudeStr ? parseFloat(longitudeStr) : null;
        PrevServiceArea = {
          type: action.previousBuisnessAttributes.businessServiceAreaType,
          fullAddress:
            action.previousBuisnessAttributes.businessServiceAreaAddress,
          latitude,
          longitude,
          state: action.previousBuisnessAttributes.businessServiceAreaState,
        };
      }

      if (action.previousBuisnessAttributes.businessSkillsetBuild) {
        const skillsetBuildList = action.previousBuisnessAttributes.businessSkillsetBuild
          .split(' & ')
          .map(skillsetBuildItem => ({
            type: 'build',
            name: skillsetBuildItem,
          }));
        prevSkillset = prevSkillset.concat(skillsetBuildList);
      }
      if (action.previousBuisnessAttributes.businessSkillsetLook) {
        const skillsetLookList = action.previousBuisnessAttributes.businessSkillsetLook
          .split(' & ')
          .map(skillsetLookItem => ({
            type: 'look',
            name: skillsetLookItem,
          }));
        prevSkillset = prevSkillset.concat(skillsetLookList);
      }

      if (_.get(action, 'previousBuisnessAttributes.logo') === 'removed') {
        prevLogo = null;
      } else if (
        action.previousBuisnessAttributes.logoImage
        // &&
        // action.previousBuisnessAttributes.logoImage.length
      ) {
        prevLogo = action.previousBuisnessAttributes.logoImage.id;
      }

      if (
        _.get(action, 'previousBuisnessAttributes.coverPhotoImage') ===
        'removed'
      ) {
        prevCoverPhoto = null;
      } else if (
        action.previousBuisnessAttributes.coverPhotoImage
        // &&
        // action.businessAttributes.coverPhoto.length
      ) {
        prevCoverPhoto = action.previousBuisnessAttributes.coverPhotoImage.id;
      }
      previousBusinessDetails = {
        businessNumber: action.previousBuisnessAttributes.businessNumber,
        name: action.previousBuisnessAttributes.businessName,
        description: action.previousBuisnessAttributes.businessDescription,
        position: action.previousBuisnessAttributes.businessPosition,
        materialLocation: action.previousBuisnessAttributes.materialLocation,
        mailingAddress: prevMailingAddress,
        serviceArea: PrevServiceArea,
        skillset: prevSkillset,
        logo: prevLogo,
        coverPhoto: prevCoverPhoto,
        gallery: action.previousBuisnessAttributes.galleryImages,
        googlePlaceField: action.previousBuisnessAttributes.googlePlaceField,
        googlePlaceId: action.previousBuisnessAttributes.googlePlaceId,
      };

      testBusinessDetails = {
        ...businessDetails,
        gallery: businessDetails.gallery.split(','),
      };

      changedValues =
        previousBusinessDetails &&
        Object.keys(testBusinessDetails).filter(
          k => testBusinessDetails[k] !== previousBusinessDetails[k],
        );
    }

    if (changedValues && changedValues.length >= 1) {
      for (let i = 0; i < changedValues.length; i += 1) {
        if (changedValues[i] === 'name') {
          yield call(
            createActivity,
            action.accessToken,
            'Business Name Updated',
          );
        } else if (changedValues[i] === 'description') {
          yield call(
            createActivity,
            action.accessToken,
            'Profile Description Updated',
          );
        } else if (changedValues[i] === 'position') {
          yield call(createActivity, action.accessToken, 'Position Updated');
        } else if (changedValues[i] === 'materialLocation') {
          yield call(
            createActivity,
            action.accessToken,
            'Material Location Updated',
          );
        } else if (changedValues[i] === 'mailingAddress') {
          if (
            testBusinessDetails.mailingAddress.fullAddress !==
            previousBusinessDetails.mailingAddress.fullAddress
          ) {
            yield call(
              createActivity,
              action.accessToken,
              'Business Mailing Address Updated',
            );
          }
        } else if (changedValues[i] === 'coverPhoto') {
          yield call(createActivity, action.accessToken, 'Cover Photo Updated');
        } else if (changedValues[i] === 'logo') {
          yield call(
            createActivity,
            action.accessToken,
            'Profile Image Updated',
          );
        } else if (changedValues[i] === 'gallery') {
          if (
            testBusinessDetails.gallery.length !==
            previousBusinessDetails.gallery.length
          )
            yield call(
              createActivity,
              action.accessToken,
              'Gallery Images Updated',
            );
        } else if (changedValues[i] === 'skillset') {
          if (
            testBusinessDetails.skillset.length !==
            previousBusinessDetails.skillset.length
          )
            yield call(createActivity, action.accessToken, 'Skillset Updated');
        } else if (changedValues[i] === 'serviceArea') {
          if (action.profileType === 'service_area') {
            const changedServiceAreaValues = Object.keys(
              testBusinessDetails.serviceArea,
            ).filter(
              k =>
                testBusinessDetails.serviceArea[k] !==
                previousBusinessDetails.serviceArea[k],
            );
            if (
              changedServiceAreaValues &&
              changedServiceAreaValues.length >= 1
            ) {
              yield call(
                createActivity,
                action.accessToken,
                'Service Area Updated',
              );
            }
          }
        }
      }
    }

    const response = yield call(
      updateUserBusiness,
      action.accessToken,
      businessDetails.businessNumber,
      businessDetails,
    );

    if (isStatusOk(response)) {
      yield put(updateBusinessAttributesSuccess(action.profileType));
      yield put(
        addNotification(
          'User profile has been successfully updated.',
          NOTIFICATON_LEVELS.SUCCESS,
        ),
      );
      yield getBusinessAttributesSaga({ accessToken: action.accessToken });
    } else {
      LogRocket.captureMessage('Failed to update business attributes.', {
        source: 'updateBusinessAttributesSaga',
        message:
          response.data.message || 'Failed to update business attributes.',
      });
      yield put(
        addNotification(
          'Something is wrong. Please try again later.',
          NOTIFICATON_LEVELS.ERROR,
        ),
      );
    }
  } catch (err) {
    LogRocket.captureException(err, {
      source: 'updateBusinessAttributesSaga',
      message: 'Failed to update business attributes.',
    });
    yield put(updateBusinessAttributesFailure(action.profileType));
    yield put(
      addNotification(
        'Something is wrong. Please try again later.',
        NOTIFICATON_LEVELS.ERROR,
      ),
    );
  }
}

export function* getSkillsetOptionsSaga(action) {
  try {
    const response = yield axios({
      method: 'get',
      headers: {
        'Content-Type': 'application/json',
        'x-api-key': API_KEY_ACCOUNTS,
      },
      url: `${GET_SKILLSET_OPTIONS_API}/${action.skillsetType}`,
    });

    if (isStatusOk(response)) {
      yield put(getSkillsetOfTypeSuccess(action.skillsetType, response.data));
    } else {
      LogRocket.captureMessage('Failed to get skillset options.', {
        source: 'getSkillsetOptionsSaga',
        message: response.data.message || 'Failed to get skillset options.',
      });
      yield put(
        addNotification(
          'Failed to get skillset options. Please try again later.',
          NOTIFICATON_LEVELS.ERROR,
        ),
      );
    }
  } catch (err) {
    LogRocket.captureException(err, {
      source: 'getSkillsetOptionsSaga',
      message: 'Failed to get skillset options.',
    });
    yield put(
      addNotification(
        'Failed to get skillset options. Please try again later.',
        NOTIFICATON_LEVELS.ERROR,
      ),
    );
  }
}

export function* getJobsSaga(action) {
  try {
    if (!action.accessToken) {
      LogRocket.captureMessage('Bad access token', {
        source: 'getJobsSaga',
        message: 'Failed to get jobs: Bad access token.',
      });
      yield put(getJobsFailure());
      yield put(
        addNotification(
          'Missing access token. Please log out and try again.',
          NOTIFICATON_LEVELS.ERROR,
        ),
      );
    } else {
      const response = yield axios({
        method: 'get',
        url: USER_JOBS_API,
        data: {},
        headers: {
          Authorization: `bearer ${action.accessToken}`,
          'Content-Type': 'application/json',
          'x-api-key': API_KEY_BUILDERS,
        },
      });

      if (isStatusOk(response)) {
        yield put(getJobsSuccess(response.data.jobs));
      } else {
        LogRocket.captureMessage('Failed to get jobs.', {
          source: 'getJobsSaga',
          message: response.data.message || 'Failed to get jobs.',
        });
        yield put(getJobsFailure());
        yield put(
          addNotification(
            'Failed to get jobs. Please try again later.',
            NOTIFICATON_LEVELS.ERROR,
          ),
        );
      }
    }
  } catch (err) {
    LogRocket.captureException(err, {
      source: 'getJobsSaga',
      message: 'Failed to get jobs.',
    });
    yield put(getJobsFailure());
    yield put(
      addNotification(
        'Failed to get jobs. Please try again later.',
        NOTIFICATON_LEVELS.ERROR,
      ),
    );
  }
}

export function* createJobSaga(action) {
  try {
    if (!action.accessToken) {
      LogRocket.captureMessage('Bad access token', {
        source: 'createJobSaga',
        message: 'Failed to create job: Bad access token.',
      });
      yield put(
        addNotification(
          'Unable to authenticate your session. Please log out and try again.',
          NOTIFICATON_LEVELS.ERROR,
        ),
      );

      return;
    }

    const request = {
      ...action.payload,
      enquiryUuid: 'manually-added',
    };

    const response = yield axios({
      method: 'POST',
      url: USER_JOBS_API,
      headers: {
        Authorization: `Bearer ${action.accessToken}`,
        'Content-Type': 'application/json',
        'x-api-key': API_KEY_BUILDERS,
      },
      data: request,
    });
    if (isStatusOk(response)) {
      yield call(createActivity, action.accessToken, 'New Job Created');
      yield put(createJobSuccess(response.data));
      yield put(closeModal());
      yield put(
        addNotification(
          'Congratulations! The job has been created.',
          NOTIFICATON_LEVELS.SUCCESS,
        ),
      );
    } else {
      LogRocket.captureMessage('Failed to create job.', {
        source: 'createJobSaga',
        message: response.data.message || 'Failed to create job.',
      });
      yield put(createJobFailure());
      yield put(
        addNotification(
          'We could not create this job at the moment. Please try again later.',
          NOTIFICATON_LEVELS.ERROR,
        ),
      );
    }
  } catch (err) {
    LogRocket.captureException(err, {
      source: 'createJobSaga',
      message: 'Failed to create job.',
    });
    yield put(createJobFailure());
    yield put(
      addNotification(
        'We could not create this job at the moment. Please try again later.',
        NOTIFICATON_LEVELS.ERROR,
      ),
    );
  }
}

export function* updateJobSaga(action) {
  try {
    if (!action.accessToken) {
      LogRocket.captureMessage('Bad access token', {
        source: 'updateJobSaga',
        message: 'Failed to update job: Bad access token.',
      });
      yield put(
        addNotification(
          'Unable to authenticate your session. Please log out and try again.',
          NOTIFICATON_LEVELS.ERROR,
        ),
      );

      return;
    }

    const response = yield axios({
      method: 'PUT',
      url: `${USER_JOBS_API}/${action.jobId}`,
      headers: {
        Authorization: `Bearer ${action.accessToken}`,
        'Content-Type': 'application/json',
        'x-api-key': API_KEY_BUILDERS,
      },
      data: action.payload,
    });

    if (isStatusOk(response)) {
      yield call(createActivity, action.accessToken, 'Job Updated');
      yield put(updateJobSuccess(response.data));
      yield put(closeModal());
      yield put(
        addNotification(
          'Congratulations! The job has been updated.',
          NOTIFICATON_LEVELS.SUCCESS,
        ),
      );
    } else {
      LogRocket.captureMessage('Failed to update job', {
        source: 'updateJobSaga',
        message: response.data.message || 'Failed to update job',
      });
      yield put(updateJobFailure());
      yield put(
        addNotification(
          'We could not update this job at the moment. Please try again later.',
          NOTIFICATON_LEVELS.ERROR,
        ),
      );
    }
  } catch (err) {
    LogRocket.captureException(err, {
      source: 'updateJobSaga',
      message: 'Failed to update job.',
    });
    yield put(updateJobFailure());
    yield put(
      addNotification(
        'We could not update this job at the moment. Please try again later.',
        NOTIFICATON_LEVELS.ERROR,
      ),
    );
  }
}

export function* resendVerificationEmailSaga(action) {
  try {
    const response = yield axios({
      method: 'POST',
      url: `${RESEND_VERIFICATION_EMAIL_API}/my-build`,
      data: {
        email: action.email,
      },
      headers: {
        'Content-Type': 'application/json',
        'x-api-key': API_KEY_ACCOUNTS,
      },
    });

    if (isStatusOk(response)) {
      yield put(resendVerificatonEmailSuccess());
      yield put(changeLoginScreenAction(LOGIN_SCREENS.NORMAL));
      yield put(
        addNotification(
          'A verification email has been re-sent to the specified email address. Please click on the verification link in the email to verify your account.',
          NOTIFICATON_LEVELS.SUCCESS,
          true,
        ),
      );
    } else {
      LogRocket.captureMessage('Failed to resend verification email', {
        source: 'resendVerificationEmailSaga',
        message: response.data.message || 'Failed to resend verification email',
      });
      yield put(resendVerificatonEmailFailure());
      yield put(
        addNotification(response.data.message, NOTIFICATON_LEVELS.ERROR, true),
      );
    }
  } catch (err) {
    LogRocket.captureException(err, {
      source: 'resendVerificationEmailSaga',
      message: 'Failed to resend verification email.',
    });
    yield put(resendVerificatonEmailFailure());
    yield put(
      addNotification(
        'Oops, something is wrong. Please try again later.',
        NOTIFICATON_LEVELS.ERROR,
        true,
      ),
    );
  }
}

export function* updateNotificationSettingsSaga(action) {
  const userAttributes = {
    [action.customAttributeKey]: action.value,
  };

  try {
    const response = yield call(
      updateUserProfile,
      action.accessToken,
      userAttributes,
    );

    if (isStatusOk(response)) {
      yield put(updateNotificationSettingsSuccess(action.notificationType));
      yield put(
        addNotification(
          // eslint-disable-next-line prettier/prettier
          `The ${
            action.notificationType
          } notification setting has been successfully ${action.value.toLowerCase()}.`,
          NOTIFICATON_LEVELS.SUCCESS,
        ),
      );
    } else {
      LogRocket.captureMessage('Failed to update notification settings', {
        source: 'updateNotificationSettingsSaga',
        message:
          response.data.message || 'Failed to update notification settings',
      });
      yield put(updateNotificationSettingsFailure(action.notificationType));
      yield put(
        setUserProfileCustomAttribute(
          action.customAttributeKey,
          getNextNofificationStatus(action.value),
        ),
      );
      yield put(
        addNotification(
          // eslint-disable-next-line prettier/prettier
          `Something is wrong and the ${action.notificationType} notificaton can not be updated.`,
          NOTIFICATON_LEVELS.ERROR,
        ),
      );
    }
  } catch (err) {
    LogRocket.captureException(err, {
      source: 'updateNotificationSettingsSaga',
      message: 'Failed to update notification settings.',
    });
    yield put(updateNotificationSettingsFailure(action.notificationType));
    yield put(
      setUserProfileCustomAttribute(
        action.customAttributeKey,
        getNextNofificationStatus(action.value),
      ),
    );
    yield put(
      addNotification(
        // eslint-disable-next-line prettier/prettier
        `Something is wrong and the ${action.notificationType} notificaton can not be updated.`,
        NOTIFICATON_LEVELS.ERROR,
      ),
    );
  }
}

export function* confirmApproveBuilderSaga(action) {
  try {
    if (!action.confirmBuilderToken) {
      LogRocket.captureMessage('Bad builder token.', {
        source: 'confirmApproveBuilderSaga',
        message: 'No builder token supplied.',
      });
      yield put(confirmApproveBuilderFailure());
      yield put(
        addNotification(
          // eslint-disable-next-line prettier/prettier
          `You need to click the link in the email. No builder token supplied.`,
          NOTIFICATON_LEVELS.ERROR,
        ),
      );
      return;
    }
    const { confirmBuilderToken } = action;
    const response = yield call(confirmApproveBuilder, confirmBuilderToken);

    if (isStatusOk(response)) {
      yield put(confirmApproveBuilderSuccess());
      yield put(
        addNotification(
          // eslint-disable-next-line prettier/prettier
          `The builder has been successfully approved.`,
          NOTIFICATON_LEVELS.SUCCESS,
        ),
      );
    } else {
      LogRocket.captureMessage('Failed to confirm approved builder.', {
        source: 'confirmApproveBuilderSaga',
        message: response.data.message || 'Failed to confirm approved builder.',
      });
      yield put(confirmApproveBuilderFailure(response.data));
      yield put(
        addNotification(
          // eslint-disable-next-line prettier/prettier
          `Something is wrong and the builder can not be ${
            action.isDeactivate ? 'deactivated' : 'approved'
          }. - ${response.data.message}`,
          NOTIFICATON_LEVELS.ERROR,
        ),
      );
    }
  } catch (err) {
    LogRocket.captureException(err, {
      source: 'confirmApproveBuilderSaga',
      message: 'Failed to confirm approved builder.',
    });
    yield put(confirmApproveBuilderFailure(err));
    yield put(
      addNotification(
        // eslint-disable-next-line prettier/prettier
        `Something is wrong and the builder can not be ${
          action.isDeactivate ? 'deactivated' : 'approved'
        }.`,
        NOTIFICATON_LEVELS.ERROR,
      ),
    );
  }
}

function* loginWatcher() {
  yield takeLatest(LOGIN, loginSaga);
  yield takeLatest(LOGOUT, logoutSaga);
  yield takeLatest(CHECK_AUTHENTICATED, checkAuthSaga);
  yield takeLatest(FORGOT_PW, forgotPwSaga);
  yield takeLatest(NEW_PW_ENTERED, newPwSaga);
  yield takeLatest(RESEND_VERIFICATION_EMAIL, resendVerificationEmailSaga);
}

function* signUpWatcher() {
  yield takeLatest(SIGN_UP, signUpSaga);
}

function* getUserProfileWatcher() {
  yield takeLatest(GET_USER_PROFILE, getUserProfileSaga);
  yield takeLatest(GET_BUSINESS_ATTRIBUTES, getBusinessAttributesSaga);
}

function* updateUserProfileWatcher() {
  yield takeLatest(UPDATE_USER_PROFILE, updateUserProfileSaga);
}

function* updateBusinessAttributesWatcher() {
  yield takeLatest(UPDATE_BUSINESS_ATTRIBUTES, updateBusinessAttributesSaga);
}

function* getSkillsetOptionsWatcher() {
  yield takeEvery(GET_SKILLSET_OPTIONS_OF_TYPE, getSkillsetOptionsSaga);
}

function* getJobsWatcher() {
  yield takeEvery(GET_JOBS, getJobsSaga);
}

function* createJobWatcher() {
  yield takeLatest(CREATE_JOB, createJobSaga);
}

function* updateJobWatcher() {
  yield takeLatest(UPDATE_JOB, updateJobSaga);
}

function* navigationWatcher() {
  yield takeLatest(BUILD_TOOLS, buildToolsSaga);
}

function* updateNotificationSettingsWatcher() {
  yield takeEvery(UPDATE_NOTIFICATION_SETTINGS, updateNotificationSettingsSaga);
}

function* confirmApproveBuilderWatcher() {
  yield takeEvery(CONFIRM_APPROVE_BUILDER, confirmApproveBuilderSaga);
}

function* uploadImage(file, fileName, type) {
  try {
    console.log('UploadImage');
    const formData = new FormData();
    formData.append('file', file);
    formData.append('fileName', fileName);
    formData.append('type', type);
    const response = yield axios({
      method: 'post',
      url: UPLOAD_API,
      headers: {
        'x-api-key': API_KEY_BUILDERS,
        'Content-Type': 'multipart/form-data',
      },
      data: formData,
    });
    return response.data;
  } catch (err) {
    return {};
  }
}

export {
  loginWatcher,
  signUpWatcher,
  getUserProfileWatcher,
  updateUserProfileWatcher,
  getSkillsetOptionsWatcher,
  getJobsWatcher,
  createJobWatcher,
  updateJobWatcher,
  navigationWatcher,
  updateNotificationSettingsWatcher,
  updateBusinessAttributesWatcher,
  confirmApproveBuilderWatcher,
};

const mapBusinessResponseToBusinessAttributes = res => ({
  businessNumber: res.businessNumber,
  businessName: res.name,
  businessPosition: res.position,
  businessMailingAddress: res.mailingAddress.fullAddress,
  businessDescription: res.description,
  businessPhoneNumber: res.phoneNumber,
  businessServiceAreaAddress: res.serviceArea.fullAddress,
  businessServiceAreaCoordinates: `${res.serviceArea.latitude}, ${
    res.serviceArea.longitude
  }`,
  businessServiceAreaType: res.serviceArea.type,
  businessSkillsetBuild: getSkillsetsByType(res.skillsets, 'build'),
  businessSkillsetLook: getSkillsetsByType(res.skillsets, 'look'),
  businessApproved: res.approved,
  businessServiceAreaState: res.serviceArea.state,
  materialLocation: res.materialLocation,
  logo: res.logo,
  coverPhoto: res.coverPhoto,
  gallery: res.gallery,
  googlePlaceId: res.googlePlaceId,
  googlePlaceField: res.googlePlaceField,
  showInSearch: res.showInSearch,
});

const getSkillsetsByType = (skillsets, type) => {
  const skills = skillsets
    .filter(skillset => skillset.type === type)
    .map(skillset => skillset.name);
  return skills && skills.length > 0 ? skills.join(' & ') : '';
};
